import React from 'react';
import { X, Check, ArrowRight, Building2, Lightbulb, Layers, RotateCcw } from 'lucide-react';

const BEFORE_BG =
  'https://customer-assets.emergentagent.com/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/tvety8s6_image.png';
const AFTER_BG =
  'https://customer-assets.emergentagent.com/job_dc9e3538-f079-41ff-9232-076517c20916/artifacts/1iuz0yf3_image.png';

const BEFORE_STEPS = [
  'Search email',
  'Search Slack',
  'Search Drive',
  'Ask the project manager',
  'Recreate the decision',
  'Hope someone remembers',
];

const AFTER_STEPS = [
  'Ask the question',
  'See the source',
  'Understand the context',
  'Reuse the lesson',
  'Move forward',
];

const ARCHEN_STATS = [
  { icon: Building2, label: 'Most used vendor', value: 'Atelier Stone (24 projects)' },
  { icon: Lightbulb, label: 'Common lesson', value: 'Lead times cause 27% of delays' },
  { icon: Layers, label: 'Top material', value: 'Taj Mahal Quartzite' },
  { icon: RotateCcw, label: 'Recurring decision', value: 'Lighting spec changed in 18 projects' },
];

const BeforeAfterSection = () => {
  return (
    <section className="bg-[#FDF8F3]">
      {/* Full-bleed split */}
      <div className="relative w-full grid grid-cols-1 md:grid-cols-2 min-h-[400px] md:min-h-[440px]">
        {/* Before (left) */}
        <div data-testid="before-archen-side" className="relative overflow-hidden">
          <img
            src={BEFORE_BG}
            alt="A cluttered desk of scattered papers, samples and notes"
            className="absolute inset-0 w-full h-full object-cover grayscale brightness-90"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to right, rgba(38,30,24,0.92) 0%, rgba(38,30,24,0.82) 40%, rgba(38,30,24,0.62) 100%)',
            }}
          />
          <div className="relative z-10 h-full flex flex-col justify-center px-8 md:px-12 lg:pl-[9%] py-8">
            <p className="text-[#C7A879] text-[11px] tracking-[0.24em] uppercase mb-6">Before Archen™</p>
            <ul className="space-y-4">
              {BEFORE_STEPS.map((item) => (
                <li key={item} className="flex items-center gap-4">
                  <span className="w-7 h-7 rounded-full border border-[#9C8865]/70 flex items-center justify-center flex-shrink-0">
                    <X size={13} className="text-[#C7A879]" />
                  </span>
                  <span className="text-[17px] text-[#E8DBC6]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* After (right) */}
        <div data-testid="after-archen-side" className="relative overflow-hidden">
          <img
            src={AFTER_BG}
            alt="A calm, minimal table with a single vase in soft daylight"
            className="absolute inset-0 w-full h-full object-cover brightness-110"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to right, #EDE6DB 0%, #EDE6DB 30%, rgba(237,230,219,0.55) 52%, rgba(237,230,219,0) 78%)',
            }}
          />
          <div className="relative z-10 h-full flex flex-col justify-center px-8 md:px-12 py-8">
            <p className="text-[#7A5A2E] text-[11px] tracking-[0.24em] uppercase mb-6">After Archen™</p>
            <ul className="space-y-4">
              {AFTER_STEPS.map((item) => (
                <li key={item} className="flex items-center gap-4">
                  <span className="w-7 h-7 rounded-full bg-[#2A211B] flex items-center justify-center flex-shrink-0">
                    <Check size={13} className="text-[#F0E9DC]" />
                  </span>
                  <span className="text-[17px] text-[#1F1812] font-medium">{item}</span>
                </li>
              ))}
            </ul>

            {/* Floating Archen™ stats card */}
            <div
              data-testid="archen-stats-card"
              className="mt-10 md:mt-0 md:absolute md:top-1/2 md:right-6 lg:right-10 md:-translate-y-1/2 w-full md:w-[300px] bg-white/90 backdrop-blur-md rounded-2xl border border-white/60 shadow-xl shadow-black/10 p-7"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_archen-studio/artifacts/kz0rmfk8_logo.svg"
                alt="Archen™"
                className="h-6 w-auto mb-6"
              />
              <div className="space-y-5">
                {ARCHEN_STATS.map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-lg bg-[#F3EDE3] flex items-center justify-center flex-shrink-0">
                      <Icon size={15} className="text-[#7A5A2E]" />
                    </span>
                    <div>
                      <p className="text-[#1F1812] text-[13px] font-semibold leading-tight">{label}</p>
                      <p className="text-[#6B5E4F] text-[12.5px] leading-snug mt-0.5">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Center arrow on the seam */}
        <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-white/70 blur-2xl scale-[1.7]" />
            <div className="relative w-16 h-16 rounded-full bg-[#FDF8F3] shadow-[0_10px_34px_rgba(42,32,24,0.28)] ring-1 ring-white flex items-center justify-center">
              <ArrowRight size={23} className="text-[#2A211B]" />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </section>
  );
};

export default BeforeAfterSection;
